import { useLayoutEffect, useRef, useState, type ComponentProps } from 'react'
import { useLingui } from '@lingui/react/macro'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { Input } from '../../ui/input'
import { FieldLabelRow } from './field-label-row'
import { cn } from '@/lib/utils'
import { HORIZONTAL_CONTROL_CLASS, HORIZONTAL_LABEL_CLASS, HORIZONTAL_ROW_CLASS } from './horizontal-field'
import { MIN_AGE } from 'shared/birthdate'
import {
  dateGhost,
  dateLayout,
  dateTextProblem,
  formatIsoDate,
  maskDateText,
  parseDateText,
  stepDateText,
  type DateProblem,
} from './date-text'

/**
 * A date of birth, typed as text in the locale's own order (`31/12/1990`, `12/31/1990` in en-US). Every call site
 * is a birthdate, which neither a month grid nor the native picker is good at: the year is typed, not paged to.
 * The field holds `YYYY-MM-DD` once the text is a real date, and the raw text until then, so the form's
 * `birthdateSchema` (shared/applicant-form.ts) reports "Invalid date" instead of saving a partial one.
 */
export default function DateField({
  label,
  className,
  orientation,
  optional,
  ...props
}: ComponentProps<'input'> & {
  label: string
  orientation?: 'vertical' | 'horizontal'
  optional?: boolean
}) {
  const field = useFieldContext<string | null | undefined>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.readOnly ?? formReadOnly
  const isOptional = useIsFieldOptional(field.name, optional)
  const isHorizontal = orientation === 'horizontal'
  const { t, i18n } = useLingui()
  const layout = dateLayout(i18n.locale)
  const value = field.state.value ?? ''
  const iso = /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : undefined
  const text = iso ? formatIsoDate(iso, layout) : value
  const readOnlyDisplay = iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(i18n.locale) : '-'
  const partPlaceholder = { day: t`DD`, month: t`MM`, year: t`YYYY` }
  const ghost = isReadOnly ? '' : dateGhost(text, layout, partPlaceholder)
  // Reported while typing, before the form's own validation runs on submit; the schema words the same bounds. Never
  // for the date the form opened with: one on file from before the bounds saves as it is.
  const problem = isReadOnly || field.state.meta.isDefaultValue ? undefined : dateTextProblem(text, layout)
  const inputRef = useRef<HTMLInputElement>(null)
  // The part the arrows just stepped, selected once React has written the new text into the input.
  const [steppedSpan, setSteppedSpan] = useState<[number, number] | null>(null)
  useLayoutEffect(() => {
    if (steppedSpan) inputRef.current?.setSelectionRange(...steppedSpan)
  }, [steppedSpan])
  const minAge = String(MIN_AGE)
  const problemMessage: Record<DateProblem, string> = {
    'day': t`Invalid day`,
    'month': t`Invalid month`,
    'invalid': t`Invalid date`,
    'before-min': t`Enter a date from 1900 on`,
    'under-age': t`You must be at least ${minAge} years old`,
  }
  // Keyed by position in the whole pattern, which is what each letter stands for.
  const ghostChars = ghost.split('').map((char, offset) => ({ char, at: text.length + offset }))

  return (
    <div className={cn(className, formReadOnly && 'pointer-events-none')}>
      <div className={isHorizontal ? HORIZONTAL_ROW_CLASS : 'grid gap-2'}>
        <FieldLabelRow
          label={label}
          htmlFor={field.name}
          hasError={field.state.meta.isTouched && !field.state.meta.isValid}
          optional={isOptional}
          compactOptional={isHorizontal}
          className={isHorizontal ? HORIZONTAL_LABEL_CLASS : undefined}
        />
        <div className={isHorizontal ? HORIZONTAL_CONTROL_CLASS : undefined}>
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="bday"
              id={field.name}
              name={field.name}
              aria-invalid={!!problem || hasFieldError(field.state.meta)}
              placeholder={layout.order.map((part) => partPlaceholder[part]).join(layout.separator)}
              maxLength={10}
              value={isReadOnly ? readOnlyDisplay : text}
              onBlur={field.handleBlur}
              onChange={(e) => {
                const deleting = e.target.value.length < text.length
                const masked = maskDateText(e.target.value, layout, !deleting)
                field.handleChange(masked ? (parseDateText(masked, layout) ?? masked) : null)
              }}
              {...props}
              onKeyDown={(e) => {
                props.onKeyDown?.(e)
                if (isReadOnly || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return
                e.preventDefault()
                const caret = e.currentTarget.selectionStart ?? text.length
                const stepped = stepDateText(text, layout, caret, e.key === 'ArrowUp' ? 1 : -1)
                field.handleChange(parseDateText(stepped.text, layout) ?? stepped.text)
                setSteppedSpan(stepped.selection)
              }}
              readOnly={isReadOnly}
              tabIndex={isReadOnly ? -1 : props.tabIndex}
              className={cn(
                // The pattern below stands in for the placeholder, which stays for assistive tech only.
                'h-8 tabular-nums placeholder:text-transparent',
                isReadOnly &&
                  'border-transparent bg-transparent px-0 focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent',
              )}
            />
            {/* The rest of the pattern, right after what is typed: the typed text is drawn invisibly to take its width. */}
            {ghost && (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center overflow-hidden border border-transparent px-2.5 text-base whitespace-pre tabular-nums md:text-sm"
              >
                <span className="invisible">{text}</span>
                <span className="text-muted-foreground">
                  {ghostChars.map((ghostChar) =>
                    ghostChar.char === layout.separator ? (
                      ghostChar.char
                    ) : (
                      <DigitCell key={ghostChar.at} char={ghostChar.char} />
                    ),
                  )}
                </span>
              </span>
            )}
          </div>
          {problem ? (
            <p data-slot="form-message" className="text-destructive m-1 text-sm">
              {problemMessage[problem]}
            </p>
          ) : (
            <FieldInfo fieldState={field.state} />
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * A pattern letter in a box exactly as wide as the digit that replaces it: Plus Jakarta Sans's tabular figures are
 * all 600 units, while `D`/`M`/`A` are 742/836/652, so letters drawn plainly shifted the pattern on every digit.
 * Shrunk so `M` (the widest) all but fits its box.
 */
function DigitCell(props: { char: string }) {
  return (
    <span className="relative inline-block">
      <span className="invisible">0</span>
      <span className="absolute inset-0 flex items-center justify-center text-[0.8em]">{props.char}</span>
    </span>
  )
}
